// The keyboard shortcut map (spec §12, phase 6). One list drives both key
// handling (entries with `run`) and the help overlay (every entry). Keys that
// depend on tool state (Delete, arrows, Enter, Esc while dragging) are handled
// by the tools and listed here for reference only.
import type { Editor } from './editor.ts';

export type ShortcutGroup = 'Tools' | 'Edit' | 'Arrange' | 'View' | 'Text';

export interface Shortcut {
  group: ShortcutGroup;
  label: string;
  key?: string;            // KeyboardEvent.key, lowercase
  code?: string;           // KeyboardEvent.code, for layout-independent keys
  mod?: boolean;           // ⌘ on macOS, Ctrl elsewhere
  shift?: boolean | 'any';
  display?: string[];      // keys to show; derived from key/mod/shift when omitted
  hidden?: boolean;        // an alternate binding not worth listing twice
  run?: (ed: Editor) => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const MOD_LABEL = isMac ? '⌘' : 'Ctrl';
const SHIFT_LABEL = isMac ? '⇧' : 'Shift';

const tool = (label: string, key: string, name: Parameters<Editor['setTool']>[0]): Shortcut =>
  ({ group: 'Tools', label, key, run: (ed) => ed.setTool(name) });

export const SHORTCUTS: Shortcut[] = [
  tool('Select', 'v', 'select'),
  tool('Hand (pan)', 'h', 'hand'),
  tool('Sticky note', 'n', 'sticky'),
  tool('Card', 'c', 'card'),
  tool('Text', 't', 'text'),
  tool('Shape', 's', 'shape'),
  { group: 'Tools', label: 'Rectangle', key: 'r', run: (ed) => ed.setShape('rect') },
  { group: 'Tools', label: 'Ellipse', key: 'o', run: (ed) => ed.setShape('ellipse') },
  tool('Connector', 'l', 'connector'),
  tool('Pen', 'p', 'pen'),
  tool('Eraser', 'e', 'eraser'),
  tool('Frame', 'f', 'frame'),
  { group: 'Tools', label: 'Back to select', key: 'escape', display: ['Esc'], run: (ed) => ed.setTool('select') },
  { group: 'Tools', label: 'Pan while held', display: ['Space', 'drag'] },

  { group: 'Edit', label: 'Undo', key: 'z', mod: true, run: (ed) => ed.undo() },
  { group: 'Edit', label: 'Redo', key: 'z', mod: true, shift: true, run: (ed) => ed.redo() },
  { group: 'Edit', label: 'Redo', key: 'y', mod: true, hidden: true, run: (ed) => ed.redo() },
  { group: 'Edit', label: 'Select all', key: 'a', mod: true, run: (ed) => { ed.setTool('select'); ed.selectAll(); } },
  { group: 'Edit', label: 'Duplicate', key: 'd', mod: true, run: (ed) => ed.duplicate() },
  { group: 'Edit', label: 'Copy / cut / paste', display: [MOD_LABEL, 'C / X / V'] },
  { group: 'Edit', label: 'Delete', display: ['⌫'] },
  { group: 'Edit', label: 'More actions', display: ['Right-click'] },
  { group: 'Edit', label: 'Nudge (×10 with shift)', display: ['←', '↑', '→', '↓'] },
  { group: 'Edit', label: 'Duplicate while dragging', display: [isMac ? '⌥' : 'Alt', 'drag'] },
  { group: 'Edit', label: 'Move cards within a column, or to the next', display: [isMac ? '⌥' : 'Alt', '↑ ↓ ← →'] },
  { group: 'Edit', label: 'Drag without snapping', display: [MOD_LABEL, 'drag'] },
  { group: 'Edit', label: 'Keep proportions / snap angle', display: [SHIFT_LABEL, 'drag'] },

  { group: 'Arrange', label: 'Bring to front', key: ']', run: (ed) => ed.bringToFront() },
  { group: 'Arrange', label: 'Send to back', key: '[', run: (ed) => ed.sendToBack() },

  { group: 'View', label: 'Zoom in', key: '=', mod: true, shift: 'any', run: (ed) => ed.zoomBy(1.25) },
  { group: 'View', label: 'Zoom in', key: '+', mod: true, shift: 'any', hidden: true, run: (ed) => ed.zoomBy(1.25) },
  { group: 'View', label: 'Zoom out', key: '-', mod: true, run: (ed) => ed.zoomBy(0.8) },
  { group: 'View', label: 'Zoom to 100%', key: '0', mod: true, run: (ed) => ed.zoomTo(1) },
  { group: 'View', label: 'Zoom to fit', code: 'Digit1', shift: true, display: [SHIFT_LABEL, '1'], run: (ed) => ed.zoomToFit() },
  { group: 'View', label: 'Show / hide minimap', key: 'm', run: (ed) => ed.command('minimap') },
  { group: 'View', label: 'Keyboard shortcuts', key: '?', shift: 'any', display: ['?'], run: (ed) => ed.command('shortcuts') },
  { group: 'View', label: 'Debug overlay', display: ['`'] },

  { group: 'Text', label: 'Edit text', display: ['Enter'] },
  { group: 'Text', label: 'Bold / italic', display: [MOD_LABEL, 'B / I'] },
  { group: 'Text', label: 'Link', display: [MOD_LABEL, 'K'] },
  { group: 'Text', label: 'Bullet list', display: [SHIFT_LABEL, MOD_LABEL, '8'] },
  { group: 'Text', label: 'Finish editing', display: ['Esc'] },
  { group: 'Text', label: 'Open a link', display: [MOD_LABEL, 'click'] },
  { group: 'Text', label: 'Open a card', display: ['Enter'] },
  { group: 'Text', label: 'Play or pause audio', display: ['Enter'] },
  { group: 'Text', label: 'Move a checklist item', display: [isMac ? '⌥' : 'Alt', '↑ / ↓'] },
];

type KeyLike = Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>;

/** The runnable shortcut this key event triggers, if any. */
export function findShortcut(e: KeyLike): Shortcut | undefined {
  if (e.altKey) return undefined;
  const mod = e.metaKey || e.ctrlKey;
  const key = e.key.toLowerCase();
  return SHORTCUTS.find((s) => s.run && !!s.mod === mod &&
    (s.shift === 'any' || !!s.shift === e.shiftKey) &&
    (s.code ? e.code === s.code : key === s.key));
}

export function displayKeys(s: Shortcut): string[] {
  if (s.display) return s.display;
  const keys: string[] = [];
  if (s.shift === true) keys.push(SHIFT_LABEL);
  if (s.mod) keys.push(MOD_LABEL);
  keys.push(s.key!.length === 1 ? s.key!.toUpperCase() : s.key!);
  return keys;
}
